/**
 * fmt — Number formatting utilities
 *
 * Shared formatting functions used across the app.
 */
import { activeBcp47 } from './locale'

/**
 * Formats a number with locale-aware thousands separators and fixed decimal places.
 * Normalizes -0 to 0 to avoid displaying "-0.000".
 * For very small values (< 0.001), displays "0.00" instead of "0.000" or "-0.000".
 */
export function fmt(n: number, decimals = 3): string {
  // Normalize -0 to 0 (floating-point artifact from arithmetic)
  let safe = Object.is(n, -0) ? 0 : n
  // For very small values, show 0 to avoid "-0.000" or "0.000"
  if (Math.abs(safe) < 0.001) {
    safe = 0
  }
  return safe.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

/**
 * Formats a number with 2 decimal places (commonly used for currency).
 * Normalizes -0 to 0.
 */
export function fmt2(n: number): string {
  const safe = Object.is(n, -0) ? 0 : n
  return safe.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * Formatiert einen Preis in der übergebenen Währung mit magnitude-abhängigen Dezimalstellen.
 * - null → '—'
 * - < 0.01 → 5 Dezimalstellen (z.B. kleine Token wie MOBILE/IOT)
 * - sonst 2-4 Dezimalstellen
 *
 * Nutzt Intl.NumberFormat für korrekte Locale-Währungsformatierung.
 *
 * Issue #55: die Locale wurde bisher aus der WAEHRUNG abgeleitet
 * (EUR -> de-DE, sonst en-US) statt aus der aktiven App-Sprache — bei einem
 * englischsprachigen Konto mit EUR-Währung (oder umgekehrt) erschien so ein
 * Format, das nicht zur gewählten Sprache passte. `activeBcp47()` (siehe
 * lib/locale.ts) ist bereits der etablierte Mechanismus für alle anderen
 * Datums-/Zahl-Formatierungen der App und wird hier wiederverwendet.
 */
export function fmtPrice(n: number | null, currency = 'usd'): string {
  if (n === null) return '—'
  const curr = currency.toUpperCase()
  const locale = activeBcp47()
  if (n < 0.01) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 5,
      maximumFractionDigits: 5,
    }).format(n)
  }
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: curr,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(n)
}

/**
 * fmtCompact — kompakte Notation für große Zahlen (z.B. 12.18M).
 *
 * F-WALLET-OVERFLOW: Große MOBILE/IOT-Balances (z.B. 12,179,464.08) überliefen
 * in der Gesamtbalance-Breakdown-Karte die Nachbarspalte. Kompakte Notation
 * hält die Zellenbreite klein. Normalisiert -0 zu 0.
 */
export function fmtCompact(n: number, decimals = 2): string {
  const safe = Object.is(n, -0) ? 0 : n
  return safe.toLocaleString('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  })
}

/**
 * Formats speed in Mb/s.
 */
export function fmtSpeed(n: number): string {
  if (n === 0) return '—'
  return `${n.toFixed(0)} Mb/s`
}

/**
 * pluralize — Singular/Plural-Auswahl anhand Count.
 *
 * F15 (FIN-SWEEP-FE-7): "1 Hotspots" → "1 Hotspot", "1 Contacts" → "1 Contact".
 * Im Deutschen ist der Plural nur an der Nomen-Form erkennbar (Hotspot/Hotspots),
 * der Artikel faellt im Deutschen weg. Bei count === 1 → singular,
 * sonst → plural. Nutzt Intl.PluralRules('de-DE') damit spaeter auch
 * Locale-spezifische Varianten moeglich sind.
 *
 * @example
 * pluralize(1, 'Hotspot', 'Hotspots')   // 'Hotspot'
 * pluralize(5, 'Hotspot', 'Hotspots')   // 'Hotspots'
 * pluralize(0, 'Contact', 'Contacts')   // 'Contacts'
 */
export function pluralize(
  count: number,
  singular: string,
  plural: string,
): string {
  // F15: Plural nur bei count !== 1 (Deutsch: 0 = Plural, 1 = Singular)
  return count === 1 ? singular : plural
}